import { getAppDisplayName, isPlatformApp } from '@/apps/apps.utils';
import { getFormattedPollResponse } from '@/guides/helpers/common.util.js';
import { FilterType } from '@/types/analytics/analytics.types';
import { i18n_t } from '@/utils/i18n';

function $t (...args) {
    try {
        return i18n_t(...args);
    } catch {
        return args[0];
    }
}
import i18nDateTimeFormatter from '@/utils/i18n/i18n-datetime-formatter';
import { getI18nKeyForMetadataDisplayName } from '@/utils/i18n/i18n-metadata';
import { MOBILE_VERSION_RULES_LIST } from '@/utils/segments';
import { FRUSTRATION_TYPES } from '@/session-replay/helpers/common.util.js';
import has from 'lodash/has';
import isBoolean from 'lodash/isBoolean';

function findTag (tags, criteria) {
    if (tags) {
        return tags.find((item) => Object.keys(criteria).every((key) => item[key] === criteria[key]));
    }
}

function lookupFactKey (rule) {
    const factKey = {
        used: 'used',
        notused: 'notUsed',
        lastused: 'lastUsed',
        viewed: 'viewed',
        notviewed: 'notViewed',
        lastviewed: 'lastViewed',
        entered: 'entered',
        notentered: 'notEntered',
        delivered: 'delivered',
        notdelivered: 'notDelivered',
        opened: 'opened',
        notopened: 'notOpened',
        clicked: 'clicked',
        notclicked: 'notClicked',
        eventCount: 'eventCount',
        eventTime: 'eventTime',
        numAccounts: 'numAccounts',
        numVisitors: 'numVisitors',
        daysActive: 'daysActive',
        deadClicks: 'deadClicks',
        errorClicks: 'errorClicks',
        rageClicks: 'rageClicks',
        uTurns: 'uTurns',
    }[rule.fact];

    if (!factKey) {
        return;
    }

    return factKey;
}

function lookupOperatorKey (rule) {
    const operatorKey = {
        '==': 'isEqualTo',
        '!=': 'isNotEqualTo',
        '<=': 'isLessThanOrEqualTo',
        '>=': 'isGreaterThanOrEqualTo',
        'contains': 'contains',
        '!contains': 'doesNotContain',
        'empty': 'isEmpty',
        '!empty': 'isNotEmpty',
        'responded': 'responded',
        '!responded': 'notResponded',
        'startsWith': 'startsWith',
        '!startsWith': 'doesNotStartWith',
        'withinLast': 'withinLast',
        '!withinLast': 'notWithinLast',
        'withinNext': 'withinNext',
        '!withinNext': 'notWithinNext',
        'between': 'between',
        'listContains': 'listContains',
        '!listContains': 'notListContains',
    }[rule.operator];

    if (!operatorKey) {
        return;
    }

    return operatorKey;
}

function lookupConditionalKey (rule, overrideKey) {
    let timeKey;
    switch (rule.schema) {
        case 'counter':
            timeKey = rule.type === 'idea' ? 'time' : 'used';
            break;
        case 'application':
            timeKey = 'used';
            break;
        case 'guideElement':
        case 'emailElement':
            timeKey = 'clicked';
            break;
        default:
            timeKey = 'time';
    }

    if (overrideKey) {
        timeKey = overrideKey;
    }

    const conditionalKey = {
        'atleast': 'atleast',
        'atmost': 'atmost',
        'ever': 'ever',
        '>=': 'since',
        'since': 'since',
        '<=': 'before',
        'before': 'before',
        'withinLast': 'withinLast',
        '!withinLast': 'notWithinLast',
        'withinNext': 'withinNext',
        '!withinNext': 'notWithinNext',
        'between': 'between',
    }[rule[timeKey]];

    if (!conditionalKey) {
        return;
    }

    return conditionalKey;
}

function determineConditionalContext (rule, conditionalKey) {
    const context = {};
    switch (conditionalKey) {
        case 'since':
        case 'before':
        case '>=':
        case '<=':
            if (!rule.first && rule.value) {
                // For reasons beyond me, rule.value is in the format of YYYY-MM-DD sometimes, a datestamp effectively
                // while rule.first and rule.last are UNIX timestamps (midnight in browser's timezone)
                // We can't just Date.parse() rule.value because it is a datestamp which has no timezone context
                // and it assumes midnight in UTC which is not what we want

                // So we extract the year, month, and day from rule.value and recreate it as a date object
                // which correctly assumes the timezone of the browser
                // and allow rule.value to format the same as rule.first and rule.last
                if (!Number.isInteger(rule.value)) {
                    const [year, month, day] = rule.value.split('-').map(Number);
                    const parsedDate = new Date(year, month - 1, day); // month is 0-indexed
                    context.date = i18nDateTimeFormatter.formatShort({
                        date: parsedDate.valueOf(),
                        ignoreTimeZone: true,
                    });
                } else {
                    context.date = i18nDateTimeFormatter.formatShort({ date: rule.value, ignoreTimeZone: true });
                }
            } else {
                context.date = i18nDateTimeFormatter.formatShort({ date: rule.first, ignoreTimeZone: true });
            }

            break;
        case 'between':
            context.firstDate = i18nDateTimeFormatter.formatShort({ date: rule.first, ignoreTimeZone: true });
            context.lastDate = i18nDateTimeFormatter.formatShort({ date: rule.last, ignoreTimeZone: true });
            break;
        case 'atleast':
        case 'atmost':
            context.value = rule.value;
            break;
        case 'withinNext':
        case 'notWithinNext':
        case 'notWithinLast':
        case 'withinLast':
            context.count = rule.count;
            break;
    }

    return context;
}

export function getI18nizedDescription (rule, resources) {
    switch (rule.type) {
        case 'page':
        case 'feature':
        case 'trackType':
            return handleCountable(rule, resources);
        case 'poll':
            return handlePoll(rule, resources);
        case 'application':
            return handleApplication(rule, resources);
        case 'guide':
            return handleGuide(rule, resources);
        case 'guideElement':
            return handleGuideElement(rule, resources);
        case 'emailElement':
            return handleEmailElement(rule, resources);
        case 'journey':
            return handleJourney(rule, resources);
        case 'email':
            return handleEmail(rule, resources);
        case 'segment':
            return handleSegment(rule, resources);
        case 'feedbackRequest':
            return handleFeedbackRequest(rule, resources);
        case 'idea':
            return handleIdea(rule, resources);
        case 'survey':
            return handleSurvey(rule, resources);
        case 'guideExperiment':
            return handleGuideExperiment(rule, resources);
    }

    if (rule.schema === 'aggregation') {
        return handleAggregation(rule, resources);
    }

    return handleMetadata(rule, resources);
}

function handleAggregation (rule, resources) {
    const { apps } = resources;
    let keyPath = 'segmentRuleSummary.aggregation';

    const { appId } = rule;
    let appName;
    let appParam = {};
    if (isPlatformApp(appId)) {
        appName = getAppDisplayName(appId);
        if (appName) {
            appParam = { appName };
        }
    }
    if (!appName) {
        const app = findTag(apps, { id: rule.appId });
        appName = app?.displayName || app?.name;
        appParam = { appName };
    }
    if (appId === '*' || !appName) {
        keyPath += '.default';
        appParam = {};
    } else {
        keyPath += '.appFilter';
    }

    const factKey = lookupFactKey(rule);
    if (!factKey) return;
    keyPath += `.${factKey}`;

    const operatorKey = lookupOperatorKey(rule);
    if (!operatorKey) return;
    keyPath += `.${operatorKey}`;

    const conditionalKey = lookupConditionalKey(rule);
    if (!conditionalKey) return;
    keyPath += `.${conditionalKey}`;

    if (['withinLast', 'notWithinLast'].includes(conditionalKey)) {
        keyPath += `.${rule.granularity}`;
    }

    const i18nizedDescription = $t(keyPath, {
        entityName: rule.entityName,
        value: rule.value,
        ...appParam,
        ...determineConditionalContext(rule, conditionalKey),
    });

    if (i18nizedDescription === keyPath) {
        return;
    }

    return {
        text: i18nizedDescription,
        icon: { visitor: 'user', account: 'users', parentAccount: 'home' }[rule.kind],
        isLocalized: true, // to avoid legacy code formatting meant for unlocalized text
    };
}

function handleMetadata (rule, resources) {
    const { schema } = resources;

    const isMobileVersionRule = MOBILE_VERSION_RULES_LIST.includes(rule.id);

    let keyPath = 'segmentRuleSummary.metadata';

    let conditionalKey;
    let operatorKey;
    if (!isMobileVersionRule && ['time', 'date'].includes(rule.schema || schema)) {
        conditionalKey = lookupConditionalKey(rule, 'operator');
        if (!conditionalKey) return;
        keyPath += `.${conditionalKey}`;

        if (['withinLast', 'notWithinLast', 'withinNext', 'notWithinNext'].includes(conditionalKey)) {
            keyPath += `.${rule.granularity}`;
        }
    } else {
        operatorKey = lookupOperatorKey(rule);
        if (!operatorKey) return;
        keyPath += `.${operatorKey}`;
    }

    const [kind, group, key] = (rule.field || '').split('.');
    const i18nizedDisplayName = getI18nKeyForMetadataDisplayName(group, key, kind);

    const i18nizedDescription = $t(keyPath, {
        entityName: i18nizedDisplayName || rule.DisplayName || rule.name || key,
        value: String(rule.value),
        ...determineConditionalContext(rule, conditionalKey),
    });

    if (i18nizedDescription === keyPath) {
        return;
    }

    let icon;
    if (isMobileVersionRule) {
        icon = 'smartphone';
    } else if (rule.type === 'survey') {
        // Surveys is not officially supported
        // but we can at least show the correct icon when i18nized
        icon = 'surveys';
    } else {
        icon = { visitor: 'user', account: 'users', parentAccount: 'home' }[rule.kind];
    }

    return {
        text: i18nizedDescription,
        icon,
        isLocalized: true, // to avoid legacy code formatting meant for unlocalized text
    };
}

function handleCountable (rule, resources) {
    const { tags } = resources;

    let keyPath = `segmentRuleSummary.${rule.type}`;

    const factKey = rule.id !== 'utm' ? lookupFactKey(rule) : undefined;
    if (factKey) {
        keyPath += `.${factKey}`;
    } else if (rule.id !== 'utm') {
        return;
    }

    let operatorKey;
    if (
        [
            'eventCount',
            'eventTime',
            'daysActive',
            'numAccounts',
            'numVisitors',
            ...FRUSTRATION_TYPES,
        ].includes(factKey)
    ) {
        operatorKey = lookupOperatorKey(rule);
        if (!operatorKey) return;
        keyPath += `.${operatorKey}`;
    }

    const conditionalKey = lookupConditionalKey(rule);
    if (!conditionalKey) return;
    keyPath += `.${conditionalKey}`;

    if (['withinLast', 'notWithinLast'].includes(conditionalKey)) {
        keyPath += `.${rule.granularity}`;
    }

    const tag = findTag(tags, { id: rule.pageId || rule.featureId || rule.trackTypeId });
    const tagName = tag ? tag.name : $t('Unknown');
    const i18nContext = {
        entityName: tagName,
        ...determineConditionalContext(rule, conditionalKey),
    };

    if (operatorKey || ['atleast', 'atmost'].includes(conditionalKey)) {
        i18nContext.value = rule.value;
    }

    let i18nizedDescription = $t(keyPath, i18nContext);

    if (i18nizedDescription === keyPath) {
        return;
    }

    if (rule.eventPropertyName || (rule.eventPropertyField && has(rule, 'eventPropertyValue'))) {
        const eventPropertyDesc = createEventPropertyDescriptor(rule);
        if (!eventPropertyDesc) return;
        i18nizedDescription += ` ${eventPropertyDesc}`;
    }

    return {
        text: i18nizedDescription,
        icon: { page: 'file', feature: 'feature', trackType: 'zap' }[rule.type],
        isLocalized: true, // to avoid legacy code formatting meant for unlocalized text
    };
}

function handlePoll (rule, resources) {
    const { tags } = resources;

    const guide = findTag(tags, { id: rule.guideId });
    const poll = findTag(guide && guide.polls, { id: rule.pollId }) || { question: $t('Unknown Poll') };

    if (poll) {
        let keyPath = 'segmentRuleSummary.poll.responseTo';

        const operatorKey = lookupOperatorKey(rule);
        if (!operatorKey) return;
        keyPath += `.${operatorKey}`;

        const conditionalKey = lookupConditionalKey(rule);
        if (!conditionalKey) return;
        keyPath += `.${conditionalKey}`;

        if (['withinLast', 'notWithinLast'].includes(conditionalKey)) {
            keyPath += `.${rule.granularity}`;
        }

        const i18nContext = {
            pollQuestion: poll.question,
            ...determineConditionalContext(rule, conditionalKey),
        };

        if (operatorKey && !['responded', 'notResponded'].includes(operatorKey)) {
            i18nContext.value = getFormattedPollResponse(rule.value, poll);
        }

        const i18nizedDescription = $t(keyPath, i18nContext);

        if (i18nizedDescription === keyPath) {
            return;
        }

        return {
            text: i18nizedDescription,
            icon: 'list',
            isLocalized: true, // to avoid legacy code formatting meant for unlocalized text
        };
    }

    return { text: '' };
}

function handleApplication (rule, resources) {
    const { apps } = resources;

    let keyPath = 'segmentRuleSummary.application';
    const factKey = lookupFactKey(rule);
    if (!factKey) return;
    keyPath += `.${factKey}`;

    const conditionalKey = lookupConditionalKey(rule);
    if (!conditionalKey) return;
    keyPath += `.${conditionalKey}`;

    if (['withinLast', 'notWithinLast'].includes(conditionalKey)) {
        keyPath += `.${rule.granularity}`;
    }

    const app = findTag(apps, { id: rule.appId });
    const appName = app?.displayName || app?.name || $t('Unknown');
    const i18nContext = {
        entityName: appName,
        ...determineConditionalContext(rule, conditionalKey),
    };

    const i18nizedDescription = $t(keyPath, i18nContext);

    if (i18nizedDescription === keyPath) {
        return;
    }

    return {
        text: i18nizedDescription,
        icon: 'activity',
        isLocalized: true, // to avoid legacy code formatting meant for unlocalized text
    };
}

function handleGuide (rule, resources) {
    const { tags } = resources;

    let keyPath = 'segmentRuleSummary.guide';
    const factKey = lookupFactKey(rule);
    if (!factKey) return;
    keyPath += `.${factKey}`;

    const conditionalKey = lookupConditionalKey(rule);
    if (!conditionalKey) return;
    keyPath += `.${conditionalKey}`;

    if (['withinLast', 'notWithinLast'].includes(conditionalKey)) {
        keyPath += `.${rule.granularity}`;
    }

    const guide = findTag(tags, { id: rule.guideId });
    const guideName = guide ? guide.name : $t('Unknown');

    const i18nContext = {
        entityName: guideName,
        ...determineConditionalContext(rule, conditionalKey),
    };

    const i18nizedDescription = $t(keyPath, i18nContext);

    if (i18nizedDescription === keyPath) {
        return;
    }

    return {
        text: i18nizedDescription,
        icon: 'guide',
        isLocalized: true, // to avoid legacy code formatting meant for unlocalized text
    };
}

function handleGuideElement (rule, resources) {
    const { tags } = resources;

    let keyPath = 'segmentRuleSummary.guideElement';
    const factKey = lookupFactKey(rule);
    if (!factKey) return;
    keyPath += `.${factKey}`;

    let operatorKey;
    if (factKey === 'eventCount') {
        operatorKey = lookupOperatorKey(rule);
        if (!operatorKey) return;
        keyPath += `.${operatorKey}`;
    }

    const conditionalKey = lookupConditionalKey(rule);
    if (!conditionalKey) return;
    keyPath += `.${conditionalKey}`;

    if (['withinLast', 'notWithinLast'].includes(conditionalKey)) {
        keyPath += `.${rule.granularity}`;
    }

    const guide = findTag(tags, { id: rule.guideId });
    const guideName = guide ? guide.name : $t('Unknown');

    const i18nContext = {
        entityName: guideName,
        ...determineConditionalContext(rule, conditionalKey),
    };

    if (operatorKey) {
        i18nContext.value = rule.value;
    }

    const i18nizedDescription = $t(keyPath, i18nContext);

    if (i18nizedDescription === keyPath) {
        return;
    }

    return {
        text: i18nizedDescription,
        icon: 'guideElement',
        isLocalized: true, // to avoid legacy code formatting meant for unlocalized text
    };
}

export function handleEmailElement (rule, resources) {
    const { tags } = resources;

    let keyPath = 'segmentRuleSummary.emailElement';
    const factKey = lookupFactKey(rule);
    if (!factKey) return;
    keyPath += `.${factKey}`;

    let operatorKey;
    if (factKey === 'eventCount') {
        operatorKey = lookupOperatorKey(rule);
        if (!operatorKey) return;
        keyPath += `.${operatorKey}`;
    }

    const conditionalKey = lookupConditionalKey(rule);
    if (!conditionalKey) return;
    keyPath += `.${conditionalKey}`;

    if (['withinLast', 'notWithinLast'].includes(conditionalKey)) {
        keyPath += `.${rule.granularity}`;
    }

    const email = findTag(tags, { id: rule.emailId });
    const emailName = email ? email.name : $t('Unknown');

    const i18nContext = {
        entityName: emailName,
        ...determineConditionalContext(rule, conditionalKey),
    };

    if (operatorKey) {
        i18nContext.value = rule.value;
    }

    const i18nizedDescription = $t(keyPath, i18nContext);

    if (i18nizedDescription === keyPath) {
        return;
    }

    return {
        text: i18nizedDescription,
        icon: 'guideElement',
        isLocalized: true, // to avoid legacy code formatting meant for unlocalized text
    };
}

function handleJourney (rule, resources) {
    const { tags } = resources;

    let keyPath = 'segmentRuleSummary.journey';
    const factKey = lookupFactKey(rule);
    if (!factKey) return;
    keyPath += `.${factKey}`;

    const conditionalKey = lookupConditionalKey(rule);
    if (!conditionalKey) return;
    keyPath += `.${conditionalKey}`;

    if (['withinLast', 'notWithinLast'].includes(conditionalKey)) {
        keyPath += `.${rule.granularity}`;
    }

    const journey = findTag(tags, { staticSegmentId: rule.staticSegmentId });
    const journeyName = journey ? journey.name : $t('Unknown');

    const i18nContext = {
        entityName: journeyName,
        ...determineConditionalContext(rule, conditionalKey),
    };
    const i18nizedDescription = $t(keyPath, i18nContext);

    if (i18nizedDescription === keyPath) {
        return;
    }

    return {
        text: i18nizedDescription,
        icon: 'orchestrate',
        isLocalized: true, // to avoid legacy code formatting meant for unlocalized text
    };
}

function handleEmail (rule, resources) {
    const { tags } = resources;

    let keyPath = 'segmentRuleSummary.email';
    const factKey = lookupFactKey(rule);
    if (!factKey) return;
    keyPath += `.${factKey}`;

    const conditionalKey = lookupConditionalKey(rule);
    if (!conditionalKey) return;
    keyPath += `.${conditionalKey}`;

    if (['withinLast', 'notWithinLast'].includes(conditionalKey)) {
        keyPath += `.${rule.granularity}`;
    }

    const email = findTag(tags, { id: rule.emailId });
    const emailName = email ? email.name : $t('Unknown');

    const i18nContext = {
        entityName: emailName,
        ...determineConditionalContext(rule, conditionalKey),
    };
    const i18nizedDescription = $t(keyPath, i18nContext);

    if (i18nizedDescription === keyPath) {
        return;
    }

    return {
        text: i18nizedDescription,
        icon: 'mail',
        isLocalized: true, // to avoid legacy code formatting meant for unlocalized text
    };
}

function handleSegment (rule, resources) {
    const { segments } = resources;

    let keyPath = 'segmentRuleSummary.segment';
    const operatorKey = lookupOperatorKey(rule);
    if (!operatorKey) return;
    keyPath += `.${operatorKey}`;

    const segment = findTag(segments, { id: rule.segmentId });
    const segmentName = segment ? segment.name : $t('Unknown segment');
    const clsString = 'segment';

    const i18nContext = {
        entityName: segmentName,
    };

    const i18nizedDescription = $t(keyPath, i18nContext);

    if (i18nizedDescription === keyPath) {
        return;
    }

    return {
        text: i18nizedDescription,
        clsString,
        entityName: segmentName,
        entityRoute: { name: 'Segments', query: { segment: rule.segmentId } },
        entityDeleted: !segment,
        segmentId: rule.segmentId,
        icon: 'pie-chart',
        isLocalized: true, // to avoid legacy code formatting meant for unlocalized text
    };
}

function handleFeedbackRequest (rule, _resources) {
    const keyPath = 'segmentRuleSummary.feedbackRequest.knownFeedbackRequest';

    let i18nizedDescription = $t('segmentRuleSummary.feedbackRequest.unknownFeedbackRequest');
    if (rule.feedbackRequestId) {
        i18nizedDescription = $t(keyPath, {
            entityName: rule.feedbackRequestId,
        });
    }

    return {
        text: i18nizedDescription,
        icon: 'lightbulb',
        isLocalized: true, // to avoid legacy code formatting meant for unlocalized text
    };
}

function handleSurvey (rule, { tags }) {
    const question = findTag(tags, { id: rule.surveyQuestionId });

    // Same as handlePoll from this point
    if (question) {
        let keyPath = 'segmentRuleSummary.poll.responseTo';

        const operatorKey = lookupOperatorKey(rule);
        if (!operatorKey) return;
        keyPath += `.${operatorKey}`;

        const conditionalKey = lookupConditionalKey(rule);
        if (!conditionalKey) return;
        keyPath += `.${conditionalKey}`;

        if (['withinLast', 'notWithinLast'].includes(conditionalKey)) {
            keyPath += `.${rule.granularity}`;
        }

        let { value } = rule;

        for (let i = 0; i < question.surveyAnswerAnalyticalValues.length; i++) {
            if (question.surveyAnswerAnalyticalValues[i] === rule.value) {
                value = question.surveyAnswerDisplayTexts[i];
                break;
            }
        }

        const i18nContext = {
            pollQuestion: question.surveyItemText,
            ...determineConditionalContext(rule, conditionalKey),
            value,
        };

        const i18nizedDescription = $t(keyPath, i18nContext);

        if (i18nizedDescription === keyPath) {
            return;
        }

        return {
            text: i18nizedDescription,
            icon: 'surveys',
            isLocalized: true, // to avoid legacy code formatting meant for unlocalized text
        };
    }

    return { text: '' };
}

function handleIdea (rule, _resources) {
    const ideaImportanceNameMap = {
        'any': 'any',
        'positive': 'mustHaveOrNiceToHave',
        '1': 'niceToHave',
        '2': 'mustHave',
        '-1': 'notInterested',
    };

    let keyPath = 'segmentRuleSummary.idea';

    if (rule.hasVoted) {
        keyPath += '.voted';
    } else {
        keyPath += '.notVoted';
    }

    if (rule.ideaId === '*') {
        keyPath += '.anyIdea';
    } else {
        keyPath += '.specificIdea';
    }

    const importance = String(rule.importance);
    if (rule.hasVoted && ideaImportanceNameMap[importance]) {
        keyPath += `.${ideaImportanceNameMap[importance]}`;
    }

    const conditionalKey = lookupConditionalKey(rule);
    if (!conditionalKey) return;
    keyPath += `.${conditionalKey}`;

    if (['withinLast', 'notWithinLast'].includes(conditionalKey)) {
        keyPath += `.${rule.granularity}`;
    }

    const i18nContext = {
        entityName: rule.ideaId,
        ...determineConditionalContext(rule, conditionalKey),
    };

    const i18nizedDescription = $t(keyPath, i18nContext);

    if (i18nizedDescription === keyPath) {
        return;
    }

    return {
        text: i18nizedDescription,
        icon: 'lightbulb',
        isLocalized: true, // to avoid legacy code formatting meant for unlocalized text
    };
}

function createEventPropertyDescriptor (rule) {
    const { eventPropertyName, eventPropertyField, eventPropertyOperator, eventPropertyValue, filterType } = rule;
    const displayName = eventPropertyName || eventPropertyField;

    let includesEventPropertyName = true;
    let initialKey = 'withEventProperty';
    if (filterType === FilterType.Utm) {
        initialKey = 'withSessionProperty';
    } else if (filterType === FilterType.Location && eventPropertyField === 'region') {
        includesEventPropertyName = false;
        initialKey = 'withRegion';
    } else if (filterType === FilterType.Location && eventPropertyField === 'country') {
        includesEventPropertyName = false;
        initialKey = 'withCountry';
    } else if (filterType === FilterType.EventTopLevel && eventPropertyField === 'server') {
        includesEventPropertyName = false;
        initialKey = 'withServerName';
    } else if (filterType === FilterType.PageParameter && eventPropertyField === 'parameters.parameter') {
        includesEventPropertyName = false;
        initialKey = 'withPageParameter';
    }

    if (isBoolean(eventPropertyValue)) {
        const keyPath = `segmentRuleSummary.${initialKey}.isEqualTo`;

        if (includesEventPropertyName) {
            return $t(keyPath, {
                eventPropertyName: displayName,
                eventPropertyValue: eventPropertyValue.toString(),
            });
        }

        return $t(keyPath, {
            eventPropertyValue: eventPropertyValue.toString(),
        });
    }

    const operatorKey = lookupOperatorKey({ operator: eventPropertyOperator });
    if (!operatorKey) return;

    const i18nContext = {};
    if (includesEventPropertyName) {
        i18nContext.eventPropertyName = displayName;
    }

    if (operatorKey !== 'isEmpty' && operatorKey !== 'isNotEmpty') {
        i18nContext.eventPropertyValue = eventPropertyValue;
    }

    const keyPath = `segmentRuleSummary.${initialKey}.${operatorKey}`;
    const eventPropertyDescriptor = $t(keyPath, i18nContext);

    if (eventPropertyDescriptor === keyPath) {
        return;
    }

    return eventPropertyDescriptor;
}

function handleGuideExperiment (rule, resources) {
    const { tags } = resources;

    const experiment = findTag(tags, { id: rule.experimentId });
    let experimentName = $t('Unknown');
    const variantName = rule.variantName || $t('Unknown');
    let conversionString = '';
    const conversionStatus = rule.experimentConverted;
    if (conversionStatus) {
        conversionString = `Conversion${conversionStatus}`;
    }
    if (experiment) {
        experimentName = experiment.name;
    }

    const i18nContext = {
        experimentName,
        variantName,
    };
    const keyPath = `segmentRuleSummary.guideExperiment.variant${variantName}${conversionString}`;

    const i18nizedDescription = $t(keyPath, i18nContext);

    if (i18nizedDescription === keyPath) {
        return;
    }

    return {
        text: i18nizedDescription,
        icon: 'beaker',
        isLocalized: true, // to avoid legacy code formatting meant for unlocalized text
    };
}
